<template>
    <Rows-container 
        ref="rowsContainer" 
        :style="cssVarsRowContainer" 
        :items-props="partLines"
        container-class="lines-home"
        @on-down="onDownRows"
    >
        <template v-for="(item, index) in partLines" :key="item.lineId">
            <line-view-skeleton v-if="item.cardItems === null" :name="item.skeleton" />
            <row-view
                v-if="checkExist(item)"
                :ref="item.ref"
                :index-row="index"
                :key-data="item.keyData"
                :item="item"
                :items="item.cardItems"
                :line-id="item.lineId"
                :title="item.title"
                :enable-program-info="props.enableProgramInfo"
                @loaded-line="onLoadedLine"
                @list-item-selected="itemsSelected"
                @show-all="showAll"
                @program-info="onProgramInfo"
            />
        </template>
    </Rows-container>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, type ComputedRef, watch, nextTick} from "vue"
    import { AppConstants } from "@/common";
    import type { RowsContainerRef } from "@/components/RowsContainer/types";
    import RowsContainer from "@/components/RowsContainer/RowsContainer.vue";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { useLinesHomeStore } from "@/stores/LinesHome";
    import type { RowConfig, IEventSelected } from "@/components/RowView/types";
    import { storeToRefs } from 'pinia';
    import LineViewSkeleton from "@/components/Skeleton/LineViewSkeleton.vue";
    import { useParentControlStore } from "@/stores/ParentControl";
    import type { ExposeInterface } from "./types";
    import { useHomePageStore } from "@/stores/HomePage";
    import { ComponentName as SkeletonName } from "@/components/Skeleton/types";
    import type { IProgramInfo } from "@/composables/useProgramInfo/types";

    const HomePageStore = useHomePageStore();
    const {
        getContinueWatchingTitle,
        getSportsTitle,
        getKidsTitle,
        getNewsTitle,
        getOlympicsTitle,
        getWatchAgainTitle,
        getTrendsTitle,
        getRecentlyOnDemandTitle,
        getMoviesTitle,
        getRecentChannelsTitle,
        getLiveSeriesTitle,
    } = storeToRefs(HomePageStore);
    const { getEnableProgramInfo, initProgramInfo, setProgramInfo } = HomePageStore;

    const ParentControlStoreModule = useParentControlStore();
    const {
        ShowParentModal,
        SetTargetWidgetAdultControl,
        SetIncorrectAdultPin,
    } = ParentControlStoreModule;
    const {
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl,
    } = storeToRefs(ParentControlStoreModule);

    const LinesHomeStore = useLinesHomeStore();
    const {
        getCardsTrends,
        getCardsWatchAgain,
        getCardsRecentlyOnDemand,
        getCardsContinueWatching,
        getCardsNews,
        getCardsKids,
        getCardsSports,
        getCardsMovies,
        getCardsOlympics,
        getCounterLinesHome,
        getCardsRecentChannels,
        getCardsLiveSeries,
    } = storeToRefs(LinesHomeStore);

    const { increaseCounterLinesHome } = LinesHomeStore;

    const props = defineProps({
        /**
         * The flag to indicate if the program info should be enabled
         */
         enableProgramInfo: {
            type: Boolean,
            required: false,
            default: false
        },
    });

    const { lineViewRows } = useAppConfig();
    const emit = defineEmits(['show-all', 'list-item-selected', 'ready', 'item-focused']);
    const selectedItem: Ref<IEventSelected | null> = ref(null);

    const rowsContainer: Ref<RowsContainerRef | null> = ref(null); 
    const { setCurrentTimes, checkParentControlCard } = useParentControl();
    const name = "LinesHome";
    const { shelfKeys, HomeCounterLines }: typeof AppConstants = AppConstants;

    const allLines: ComputedRef<RowConfig[]> = computed(() => {
        return [
            {
                keyData: shelfKeys.recentChannelsCards,
                title: getRecentChannelsTitle.value,
                ref: shelfKeys.recentChannelsCards,
                lineId: shelfKeys.recentChannelsCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsRecentChannels.value,
            },
            {
                keyData: shelfKeys.olympicsCards,
                title: getOlympicsTitle.value,
                ref: shelfKeys.olympicsCards,
                lineId: shelfKeys.olympicsCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsOlympics.value,
            },
            {
                keyData: shelfKeys.moviesCards,
                title: getMoviesTitle.value,
                ref: shelfKeys.moviesCards,
                lineId: shelfKeys.moviesCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsMovies.value,
            },
                {
                keyData: shelfKeys.sportsCards,
                title: getSportsTitle.value,
                ref: shelfKeys.sportsCards,
                lineId: shelfKeys.sportsCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsSports.value,
            },
            {
                keyData: shelfKeys.kidsCards,
                title: getKidsTitle.value,
                ref: shelfKeys.kidsCards,
                lineId: shelfKeys.kidsCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsKids.value,
            },
            {
                keyData: shelfKeys.newsCards,
                title: getNewsTitle.value,
                ref: shelfKeys.newsCards,
                lineId: shelfKeys.newsCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsNews.value,
            },
            {
                keyData: shelfKeys.continueWatchingCards,
                title: getContinueWatchingTitle.value,
                ref: shelfKeys.continueWatchingCards,
                lineId: shelfKeys.continueWatchingCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsContinueWatching.value,
            },
            {
                keyData: shelfKeys.recentlyOnDemandCards,
                title: getRecentlyOnDemandTitle.value,
                ref: shelfKeys.recentlyOnDemandCards,
                lineId: shelfKeys.recentlyOnDemandCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsRecentlyOnDemand.value,
            },
            {
                keyData: shelfKeys.watchAgainCards,
                title: getWatchAgainTitle.value,
                ref: shelfKeys.watchAgainCards,
                lineId: shelfKeys.watchAgainCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsWatchAgain.value,
            },
            {
                keyData: shelfKeys.trendsCards,
                title: getTrendsTitle.value,
                ref: shelfKeys.trendsCards,
                lineId: shelfKeys.trendsCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsTrends.value,
            },
            {
                keyData: shelfKeys.liveSeries,
                title: getLiveSeriesTitle.value,
                ref: shelfKeys.liveSeries,
                lineId: shelfKeys.liveSeries,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsLiveSeries.value,
            }
        ].filter((item) => lineViewRows.home.includes(item.keyData))
        .sort((a, b) => lineViewRows.home.indexOf(a.keyData) - lineViewRows.home.indexOf(b.keyData));
    });
    const partLines: ComputedRef<RowConfig[]> = computed(() => allLines.value.slice(0, getCounterLinesHome.value));
    const cssVarsRowContainer = computed((): {[key: string]: string}  => {
        return {'--amount-line': String(allLines.value.length)}
    });

    const checkExist = (item: RowConfig): boolean => {
        const isExist = item.cardItems !== null && item.cardItems.length > 0;
        if (!isExist) {
            onLoadedLine();
        } 
        return isExist;
    };
    
    const counter = ref(1);
    const onLoadedLine = (): void => {
        if (rowsContainer.value === null) {
            return;
        }
        const countLoadedRows = rowsContainer.value.getCountLoadedRows();
        if (countLoadedRows !== null && getCounterLinesHome.value >= countLoadedRows) {
            nextTick(() => {
                emit('ready');
            });
        }

        if (counter.value <= HomeCounterLines) {
            const isIncreasing = (counter.value % HomeCounterLines) != 0; 
            if (isIncreasing) {
                increaseCounterLinesHome();
                counter.value++;
            }
        }
    };

    const onListItemSelected = (): void => {
        emit("list-item-selected", selectedItem.value);
    };
    const itemsSelected = (event: IEventSelected): void => {
        selectedItem.value = event;

        checkParentControlCard(event).then((isAllow)=>{
            if (isAllow) {
                onListItemSelected();
            } else {
                ShowParentModal();
                SetTargetWidgetAdultControl(name);
            }
        });
    };

    const onDownRows = (): void => {
        if (getCounterLinesHome.value < allLines.value.length) {
            increaseCounterLinesHome();
        }
    };

    const showAll = (event: IEventSelected): void => {
        emit("show-all", event);
    };

    const focusFirstRow = (): void => {
        if (rowsContainer.value) {
            rowsContainer.value.focusFirstRow();
        }
    };

    const onProgramInfo = (event: IProgramInfo): void => {
        setProgramInfo(event);
    };

    watch(
        isAdultPinCorrect, 
        (value: boolean): void => {
            if (value && name === targetWidgetAdultControl.value) {
                SetIncorrectAdultPin();
                setCurrentTimes();
                onListItemSelected();
            }
        }
    );

    if (getEnableProgramInfo) {
        const lineForProgramInfo: ComputedRef<RowConfig | null> = computed(() => {
            return partLines.value.find((item) => item.cardItems && item.cardItems.length > 0) ?? null;
        });

        if (lineForProgramInfo.value !== null && lineForProgramInfo.value?.cardItems !== null) {
            initProgramInfo(lineForProgramInfo.value.cardItems[0]).catch(() => {});
        } else {
            watch(
                lineForProgramInfo, 
                (row: RowConfig | null): void=> {
                    if (row !== null && row.cardItems !== null) {
                        initProgramInfo(row.cardItems[0]).catch(() => {});
                    }
                },
                {once: true}
            );
        }
    }

    defineExpose<ExposeInterface>({
        focusFirstRow
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>